/**
 * LecturesFeature — home page shows the N most recent published CMS lectures
 * as a 3-col card grid. Mirrors src/components/home/NewsTeaser.tsx in shape:
 * build-time snapshot data, locale-aware, no runtime fetch.
 *
 * The list is already sorted `event_date DESC, id DESC` server-side, so the
 * default ordering needs no work here. The `audience` setting is the only
 * filter applied — empty string (the default) means "show all".
 *
 * Audience badge labels reuse the existing `lectures.detail.tagTeacher /
 * tagParent / tagStudent` keys (src/i18n/{zh-Hant,zh-Hans,en}.json:684-686),
 * not new `home.lectures.audience.*` keys, to keep the i18n footprint small.
 *
 * Phase 1 mobile overhaul (2026-10-13): the home variant of the card was
 * extracted into `src/components/cards/LectureCard.tsx` — see that file
 * for the `compact` size used here. The archive page uses the same
 * component with `regular` size.
 */
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import { Container, Reveal } from '@/components/ui'
import { tCms } from '@/cms/tCms'
import { pickLocalized } from '@/cms/pickLocale'
import type { CmsLectureIndexEntry } from '@/content/cms/lectures.generated'
import { useCmsLectures } from '@/hooks/useCmsLectures'
import LectureCard, { type NormalizedLecture } from '@/components/cards/LectureCard'

type Audience = 'teacher' | 'parent' | 'student' | ''

interface LecturesSettings {
  limit?: number
  audience?: Audience
}

function pickLectures(
  source: CmsLectureIndexEntry[],
  audience: Audience,
  limit: number,
): CmsLectureIndexEntry[] {
  const filtered =
    audience === ''
      ? source
      : source.filter((l) => l.audience === audience)
  return filtered.slice(0, limit)
}

export default function LecturesFeature({
  settings,
}: {
  settings?: LecturesSettings
} = {}) {
  // Defaults reproduce today's compiled-in behaviour: 3 cards, all audiences.
  const limit =
    typeof settings?.limit === 'number' && settings.limit > 0 ? settings.limit : 3
  const audience: Audience =
    settings?.audience === 'teacher' ||
    settings?.audience === 'parent' ||
    settings?.audience === 'student'
      ? settings?.audience
      : ''

  // Read the locale from react-i18next, not document.documentElement.lang —
  // see the same note in NewsTeaser.tsx about the layout effect racing the
  // language switch.
  const { t, i18n } = useTranslation()
  const locale = (i18n.language || 'zh-Hant') as 'zh-Hant' | 'zh-Hans' | 'en'

  // 2026-10-16: `source` is now the live list from useCmsLectures() rather
  // than the bundled snapshot, so a lecture published after the last deploy
  // reaches the homepage without a rebuild. The hook still seeds from the
  // snapshot so the prerendered HTML and the CMS-down case both work.
  const source = useCmsLectures()
  const items = pickLectures(source, audience, limit)
  const viewAllLabel = tCms('home', 'home.lectures.viewAll', locale)

  return (
    <section
      className="py-section-y px-section-x bg-surface-2"
      aria-labelledby="lectures-title"
    >
      <Container>
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end sm:justify-between gap-3 sm:gap-4 mb-6 md:mb-7">
          <div>
            <span className="section-title__tagline inline-block mb-2 md:mb-3">
              {tCms('home', 'home.lectures.tag', locale)}
            </span>
            <h2
              id="lectures-title"
              className="section-title__title text-2xl sm:text-3xl md:text-4xl text-ink"
            >
              {tCms('home', 'home.lectures.title', locale)}
            </h2>
          </div>
          <Link
            to="/lectures"
            className="inline-flex items-center self-start sm:self-auto gap-2 text-primary font-semibold hover:gap-3 transition-all text-sm md:text-base"
          >
            <span>{viewAllLabel}</span>
            <Icon icon="lucide:arrow-right" />
          </Link>
        </div>

        {items.length === 0 ? (
          <p className="text-sm text-ink-muted text-center py-12">
            {tCms('home', 'home.lectures.empty', locale)}
          </p>
        ) : (
          <div className="grid md:grid-cols-3 gap-gap-cards">
            {items.map((lecture, idx) => {
              const venue = pickLocalized(
                locale,
                lecture.venue_zhHans ?? '',
                lecture.venue_zhHant,
                lecture.venue_en,
              )
              // 2026-10-25: was an `isZh` ternary that preferred the
              // Traditional alt for BOTH Chinese locales, so a 简体中文
              // reader was served Traditional. The alt has the same
              // three-locale shape as the fields above — use the picker.
              const photoAlt = pickLocalized(
                locale,
                lecture.photo1_alt_zhHans ?? '',
                lecture.photo1_alt_zhHant,
                lecture.photo1_alt_en,
              )
              const normalized: NormalizedLecture = {
                slug: lecture.slug,
                title: lecture.title,
                summary: lecture.summary,
                audience: (lecture.audience as NormalizedLecture['audience']) || 'teacher',
                date: lecture.event_date,
                venue,
                photoUrl: lecture.photo1_url || undefined,
                photoAlt: photoAlt || undefined,
                photoWidth: lecture.photo1_width ?? undefined,
                photoHeight: lecture.photo1_height ?? undefined,
                externalUrl: lecture.external_url || undefined,
                // 2026-10-20: the host organisation's official website. The
                // compact card wraps everything in a <Link>, so the venue
                // renders as plain text there — see VenueLink.
                venueUrl: lecture.venue_url || undefined,
              }
              return (
                <Reveal key={lecture.slug} delay={idx * 0.1}>
                  <LectureCard
                    lecture={normalized}
                    size="compact"
                    className="bg-white!"
                    ctaLabel={viewAllLabel}
                    audienceBadgeLabel={t(
                      `lectures.detail.tag${lecture.audience.charAt(0).toUpperCase()}${lecture.audience.slice(1)}`,
                      { defaultValue: lecture.audience },
                    )}
                    externalVenueLabel={t('lectures.detail.venueWebsite')}
                    locale={locale}
                  />
                </Reveal>
              )
            })}
          </div>
        )}
      </Container>
    </section>
  )
}